import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AccountGroup, ProjectionFilter } from '@galaxy/account-group';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { AccountSelectorApiService } from './account-selector-api.service';

export interface Account {
  companyName: string;
  accountGroupId: string;
}

const accountGroupsToUIAccounts = (ags: AccountGroup[]): Account[] => ags.map(accountGroupToUIAccount);

function accountGroupToUIAccount(ag: AccountGroup): Account {
  return { companyName: ag.napData.companyName, accountGroupId: ag.accountGroupId };
}

@Component({
  selector: 'sales-ui-account-selector',
  templateUrl: './account-selector.component.html',
  styleUrls: ['./account-selector.component.scss'],
})
export class AccountSelectorComponent implements OnInit {
  @Input() allowNoneOption: boolean;
  @Input() parentFormCtrl: FormControl;

  @Output() selectedAccountGroupId: EventEmitter<string> = new EventEmitter<string>();

  filteredAccounts$: Observable<Account[]>;
  accountControl: FormControl;

  constructor(private readonly accountSelectorService: AccountSelectorApiService) {}

  ngOnInit(): void {
    this.initFormControl();
    this.filteredAccounts$ = this.getFilteredAccounts();
    this.setupValueEmitter();
  }

  displayCompanyName(account?: Account): string | undefined {
    return account?.companyName || undefined;
  }

  private initFormControl(): void {
    this.accountControl = this.parentFormCtrl || new FormControl(undefined);
  }

  private getFilteredAccounts(): Observable<Account[]> {
    const pf = new ProjectionFilter({ napData: true });
    return this.accountControl.valueChanges.pipe(
      startWith<string | Account | undefined>(''),
      map((inputTerm) => {
        const noneIsSelected = inputTerm === undefined;
        if (noneIsSelected) {
          return '';
        }
        return typeof inputTerm === 'string' ? inputTerm : inputTerm.companyName;
      }),
      debounceTime(300),
      switchMap((searchTerm) => this.accountSelectorService.search(pf, searchTerm, 20)),
      map(accountGroupsToUIAccounts),
    );
  }

  private setupValueEmitter(): void {
    this.accountControl.valueChanges.subscribe((value) => {
      if (typeof value === 'string') {
        return;
      }
      this.selectedAccountGroupId.emit(value.accountGroupId);
    });
  }
}
